import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { trackComparisonSectionViewed, trackAddToCartFromComparison } from '~/scripts/analytics/events.js';

class ProductComparisonSection extends HTMLElement {
    constructor() {
        super();

        // Configuration
        this.maxProducts = parseInt(this.dataset.maxProducts) || 5;
        this.currentProductId = this.dataset.currentProductId;
        this.currentProductHandle = this.dataset.currentProductHandle;

        // State
        this.comparisonProducts = [];
        this.relatedProducts = [];
        this.currentProductInComparison = false;
        this.isLoading = false;
        this.desktopSwiper = null;
        this.mobileSwiper = null;
        this.lastBreakpoint = null;
        this.lastProductIds = '';
        this.lastRenderMode = null;
        this.currentProductData = null;

        // Cache
        this.productCache = new Map();
        
        // DOM State Management
        this.renderedHeaders = new Map(); // Track actual DOM header elements by product ID
        
        // Analytics tracking
        this.hasTrackedView = false;

        this.init();
    }

    async init() {
        try {
            // Initialize breakpoint tracking
            this.lastBreakpoint = window.innerWidth >= 768 ? 'desktop' : 'mobile';

            // Load current product data first
            await this.loadCurrentProductData();

            // Get related products from data attribute
            this.loadRelatedProducts();

            // Set up event listeners
            this.attachEventListeners();

            // Load comparison products
            await this.loadComparisonProducts();

            // Initial render
            this.render();
            
            // Set up intersection observer for analytics
            this.setupAnalyticsTracking();
        } catch (error) {
            console.error('Failed to initialize product comparison section:', error);
        }
    }
    
    setupAnalyticsTracking() {
        // Track when comparison section comes into view
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.hasTrackedView) {
                    this.hasTrackedView = true;
                    trackComparisonSectionViewed({
                        comparison_products_count: this.comparisonProducts.length
                    });
                }
            });
        }, {
            threshold: 0.3 // Trigger when 30% of section is visible
        });
        
        observer.observe(this);
    }

    loadRelatedProducts() {
        const relatedProductsData = this.querySelector('[data-related-products]');
        if (relatedProductsData) {
            try {
                this.relatedProducts = JSON.parse(relatedProductsData.textContent);
            } catch (error) {
                console.error('Failed to parse related products:', error);
            }
        }
    }

    async loadComparisonProducts() {
        // Get products from BikeComparison if available
        const bikeComparisonElement = document.querySelector('bike-comparison');

        if (bikeComparisonElement && bikeComparisonElement.bikes) {
            // Check if current product is in comparison
            this.currentProductInComparison = bikeComparisonElement.bikes.some(
                (bike) => bike.id === this.currentProductId
            );

            // If current product is in comparison, show all bikes from comparison
            // Otherwise, filter out current product and limit to max - 1
            if (this.currentProductInComparison) {
                this.comparisonProducts = bikeComparisonElement.bikes
                    .filter((bike) => bike.id !== this.currentProductId)
                    .slice(0, this.maxProducts - 1);
            } else {
                this.comparisonProducts = bikeComparisonElement.bikes.slice(0, this.maxProducts - 1);
            }
        }

        // If no comparison products, load recommendations instead of just related products
        if (this.comparisonProducts.length === 0) {
            await this.loadRecommendations();
        } else {
            // Fill remaining slots with related products for partial fills
            const remainingSlots = Math.max(0, this.maxProducts - 1 - this.comparisonProducts.length);

            if (remainingSlots > 0 && this.relatedProducts.length > 0) {
                const additionalProducts = this.relatedProducts
                    .filter((product) => !this.comparisonProducts.find((cp) => cp.id === product.id))
                    .slice(0, remainingSlots)
                    .map((product) => ({
                        id: product.id,
                        handle: product.handle,
                        title: product.title,
                        image: product.image,
                        price: product.price,
                        url: product.url
                    }));

                this.comparisonProducts = [...this.comparisonProducts, ...additionalProducts];
            }
        }

        // Fetch product data for comparison
        await this.fetchProductData();
    }

    async fetchProductData() {
        const promises = this.comparisonProducts.map(async (product) => {
            if (!this.productCache.has(product.handle)) {
                try {
                    const response = await fetch(`/products/${product.handle}?view=comparison`);
                    if (response.ok) {
                        const html = await response.text();
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(html, 'text/html');
                        const dataScript = doc.querySelector('#product-comparison-data');

                        if (dataScript) {
                            const data = JSON.parse(dataScript.textContent);
                            this.productCache.set(product.handle, data);
                        }
                    }
                } catch (error) {
                    console.error(`Failed to fetch data for ${product.handle}:`, error);
                }
            }
        });

        await Promise.all(promises);
    }

    async loadRecommendations() {
        // Use Shopify's native recommendation API
        const recommendations = await this.fetchShopifyRecommendations();
        this.comparisonProducts = recommendations;
    }

    async fetchShopifyRecommendations() {
        if (!this.currentProductId) {
            console.error('No current product ID available for recommendations');
            throw new Error('Missing current product ID');
        }

        // Section rendering API - render THIS section with recommendations
        const sectionId = 'product-comparison-section';
        const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?product_id=${this.currentProductId}&section_id=${sectionId}&limit=10&intent=related`;

        const response = await fetch(url, {
            priority: 'low'
        });

        if (!response.ok) {
            console.error(`Section rendering failed: ${response.status} ${response.statusText}`);
            throw new Error(`Section Rendering API failed: HTTP ${response.status}: ${response.statusText}`);
        }

        const html = await response.text();
        const result = this.parseRecommendationsFromSection(html);

        if (!result || result.length === 0) {
            return [];
        }

        return result;
    }

    parseRecommendationsFromSection(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find our JSON data with collections
        const dataScript = doc.querySelector('[data-recommendations-with-collections]');
        if (!dataScript) {
            console.error('No recommendations data found in section HTML');
            return [];
        }

        const data = JSON.parse(dataScript.textContent);

        if (!data?.products?.length) {
            return [];
        }

        // Map products with collections from Liquid
        const allRecommendations = data.products
            .filter((product) => product.handle !== this.currentProductHandle)
            .map((product) => ({
                id: product.id?.toString(),
                handle: product.handle,
                title: product.title,
                price: product.price,
                image: product.featured_image,
                url: product.url,
                collections: product.collections || [],
                isRecommendation: true
            }));

        // Filter by shared collections
        const filtered = this.filterBySharedCollections(allRecommendations);
        return filtered.slice(0, 3);
    }

    filterBySharedCollections(recommendations) {
        // Get current product's collections
        const currentCollections = this.getCurrentProductCollections();

        if (!currentCollections || currentCollections.length === 0) {
            return [];
        }

        // Target collection handles we care about for comparison
        const targetCollections = ['fatbikes', 'elektrische-fatbikes', 'e-bikes', 'e-steps', 'onderdelen'];

        // Find which target collections the current product belongs to
        const currentTargetCollections = currentCollections.filter((handle) =>
            targetCollections.includes(handle)
        );

        if (currentTargetCollections.length === 0) {
            return [];
        }

        // Filter recommendations to only products that share target collections
        const relevantProducts = recommendations.filter((product) => {
            const productCollections = product.collections || [];

            // Check if this product shares any target collections with current product
            const sharedTargetCollections = productCollections.filter((handle) =>
                currentTargetCollections.includes(handle)
            );

            return sharedTargetCollections.length > 0;
        });

        return relevantProducts;
    }

    getCurrentProductCollections() {
        // Try to get collections from current product data first
        if (this.currentProductData && this.currentProductData.collections) {
            return this.currentProductData.collections;
        }

        // Fallback: try to detect from URL path
        const urlPath = window.location.pathname;
        const collectionMatch = urlPath.match(/\/collections\/([^\/]+)/);
        if (collectionMatch) {
            return [collectionMatch[1]];
        }

        return [];
    }

    attachEventListeners() {
        // Add current product to comparison
        this.querySelectorAll('[data-add-current-to-comparison]').forEach((button) => {
            button.addEventListener('click', this.handleAddToComparison.bind(this));
        });

        // Add to cart buttons
        this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
            button.addEventListener('click', this.handleAddToCart.bind(this));
        });

        // Remove current product from comparison
        this.querySelectorAll('[data-remove-current-from-comparison]').forEach((button) => {
            button.addEventListener('click', this.handleRemoveCurrentFromComparison.bind(this));
        });

        // Remove product delegated event
        this.addEventListener('click', (e) => {
            if (e.target.closest('[data-remove-product]')) {
                const slide = e.target.closest('.swiper-slide') || e.target.closest('th');
                const productId = slide?.dataset.productId;
                if (productId) {
                    this.removeProduct(productId);
                }
            }

            // Add recommendation to comparison
            if (e.target.closest('[data-add-recommendation-to-comparison]')) {
                this.handleAddRecommendationToComparison(e);
            }
        });

        // Window resize event
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);
    }

    handleResize() {
        // Debounce resize events
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            // Check if we crossed a breakpoint
            const currentBreakpoint = window.innerWidth >= 768 ? 'desktop' : 'mobile';

            // Only re-render if we crossed a breakpoint
            if (this.lastBreakpoint !== currentBreakpoint) {
                this.lastBreakpoint = currentBreakpoint;
                this.render();
            }
        }, 150);
    }

    handleAddToComparison(e) {
        const button = e.currentTarget;
        const productData = {
            id: button.dataset.productId,
            handle: button.dataset.productHandle,
            title: button.dataset.productTitle,
            image: button.dataset.productImage
        };

        // Use global bikeComparison API
        if (window.bikeComparison && window.bikeComparison.add) {
            const added = window.bikeComparison.add(productData);
            if (added) {
                // Update button state
                button.textContent =
                    window.themeVariables?.strings?.product_comparison?.added_to_comparison || 'Added!';
                button.classList.add('bg-green-600', 'hover:bg-green-700');
                button.classList.remove('bg-blue-600', 'hover:bg-blue-700');
                button.disabled = true;

                // Reset after 2 seconds
                setTimeout(() => {
                    button.textContent =
                        window.themeVariables?.strings?.product_comparison?.add_to_comparison ||
                        'Add to comparison';
                    button.classList.remove('bg-green-600', 'hover:bg-green-700');
                    button.classList.add('bg-blue-600', 'hover:bg-blue-700');
                    button.disabled = false;
                }, 2000);

                // Reload comparison products
                this.loadComparisonProducts().then(() => this.render());
            }
        }
    }

    handleAddToCart(e) {
        const button = e.currentTarget;
        const productHandle = button.dataset.productHandle;

        if (!productHandle) {
            console.error('No product handle found for add to cart');
            return;
        }

        // If this is the current product, open customization drawer
        if (productHandle === this.currentProductHandle) {
            this.openCustomizationDrawer();
            
            // Track analytics for current product customization
            trackAddToCartFromComparison({
                product_handle: productHandle,
                source: 'comparison_section',
                is_current_product: true,
                comparison_products_count: this.comparisonProducts.length
            });
            return;
        }

        // For other products, add directly to cart
        // Get the current variant (assume first available for now)
        fetch(`/products/${productHandle}.js`)
            .then((response) => response.json())
            .then((product) => {
                if (product.variants && product.variants.length > 0) {
                    const variant = product.variants.find((v) => v.available) || product.variants[0];
                    return this.addToCart(variant.id, 1);
                }
                throw new Error('No available variant found');
            })
            .then((response) => {
                // Track analytics for comparison product cart addition
                trackAddToCartFromComparison({
                    product_handle: productHandle,
                    variant_id: response.variant_id,
                    source: 'comparison_section',
                    is_current_product: false,
                    comparison_products_count: this.comparisonProducts.length
                });
                
                // Update button to show success
                const originalText = button.textContent;
                button.textContent = 'Added!';
                button.classList.remove('bg-green-600', 'hover:bg-green-700');
                button.classList.add('bg-green-700', 'hover:bg-green-800');
                button.disabled = true;

                // Reset after 2 seconds
                setTimeout(() => {
                    button.textContent = originalText;
                    button.classList.remove('bg-green-700', 'hover:bg-green-800');
                    button.classList.add('bg-green-600', 'hover:bg-green-700');
                    button.disabled = false;
                }, 2000);

                // Trigger cart update event if available
                if (window.theme && window.theme.cart) {
                    window.theme.cart.refresh();
                }
            })
            .catch((error) => {
                console.error('Failed to add to cart:', error);

                // Show error state
                const originalText = button.textContent;
                button.textContent = 'Error';
                button.classList.add('bg-red-600', 'hover:bg-red-700');
                button.classList.remove('bg-green-600', 'hover:bg-green-700');

                setTimeout(() => {
                    button.textContent = originalText;
                    button.classList.remove('bg-red-600', 'hover:bg-red-700');
                    button.classList.add('bg-green-600', 'hover:bg-green-700');
                }, 2000);
            });
    }

    openCustomizationDrawer() {
        // Try different methods to open the customization drawer

        // Method 1: Look for product form and trigger its add to cart
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
            const addToCartButton = productForm.querySelector('[type="submit"], [data-add-to-cart]');
            if (addToCartButton) {
                addToCartButton.click();
                return;
            }
        }

        // Method 2: Look for customization drawer trigger
        const drawerTrigger = document.querySelector(
            '[data-drawer-trigger="customization"], [data-open-customization]'
        );
        if (drawerTrigger) {
            drawerTrigger.click();
            return;
        }

        // Method 3: Dispatch custom event
        const customizationEvent = new CustomEvent('openCustomization', {
            detail: { productHandle: this.currentProductHandle }
        });
        document.dispatchEvent(customizationEvent);

        // Fallback: scroll to product form
        if (productForm) {
            productForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    async addToCart(variantId, quantity = 1) {
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
                id: variantId,
                quantity: quantity
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    handleRemoveCurrentFromComparison(e) {
        const button = e.currentTarget;
        const productId = button.dataset.productId;

        if (!productId) {
            console.error('No product ID found for remove from comparison');
            return;
        }

        // Remove from BikeComparison
        if (window.bikeComparison && window.bikeComparison.remove) {
            window.bikeComparison.remove(productId);
        }

        // Update state and re-render
        this.currentProductInComparison = false;
        this.loadComparisonProducts().then(() => this.render());
    }

    handleAddRecommendationToComparison(e) {
        const button = e.target.closest('[data-add-recommendation-to-comparison]');
        if (!button) return;

        const productData = {
            id: button.dataset.productId,
            handle: button.dataset.productHandle,
            title: button.dataset.productTitle,
            image: button.dataset.productImage,
            price: button.dataset.productPrice,
            url: button.dataset.productUrl
        };


        // Use BikeComparison for compatibility
        if (window.bikeComparison && window.bikeComparison.add) {
            const added = window.bikeComparison.add(productData);
            if (added) {
                // Give visual feedback
                button.textContent =
                    window.themeVariables?.strings?.product_comparison?.added_to_comparison || 'Added!';
                button.classList.add('text-green-600');
                button.classList.remove('text-blue-600');
                button.disabled = true;

                // Reset after 2 seconds
                setTimeout(() => {
                    // Reload the comparison to show the new product instead of recommendation
                    this.loadComparisonProducts().then(() => this.render());
                }, 1000);
            }
        }
    }

    removeProduct(productId) {
        this.comparisonProducts = this.comparisonProducts.filter((p) => p.id !== productId);

        // Also remove from BikeComparison if present
        if (window.bikeComparison && window.bikeComparison.remove) {
            window.bikeComparison.remove(productId);
        }

        // Reload and re-render
        this.loadComparisonProducts().then(() => this.render());
    }

    render() {
        // Check if products have changed
        const currentProductIds = this.comparisonProducts
            .map((p) => p.id)
            .sort()
            .join(',');
        const hasProductsChanged = this.lastProductIds !== currentProductIds;

        if (!hasProductsChanged) {
            // Products haven't changed, just update buttons
            this.updateCurrentProductButtons();
            return;
        }

        this.lastProductIds = currentProductIds;

        // Reset render mode to force full re-render when products change
        this.lastRenderMode = null;

        this.showLoading(true);

        try {
            // Desktop rendering
            if (window.innerWidth >= 768) {
                this.renderTableView();
                this.lastRenderMode = 'table';
            } else {
                // Mobile rendering
                this.renderMobileView();
                this.lastRenderMode = 'mobile';
            }

            this.updateCurrentProductButtons();
        } finally {
            this.showLoading(false);
        }
    }

    updateCurrentProductButtons() {
        const addButtons = this.querySelectorAll('[data-add-current-to-comparison]');
        const removeButtons = this.querySelectorAll('[data-remove-current-from-comparison]');

        addButtons.forEach((button) => {
            if (this.currentProductInComparison) {
                // Hide add button completely when product is in comparison
                button.classList.add('hidden');
            } else {
                // Show add button when product is not in comparison
                button.classList.remove('hidden');
                const addText = button.querySelector('[data-add-text]');
                const inComparisonText = button.querySelector('[data-in-comparison-text]');

                if (addText) addText.classList.remove('hidden');
                if (inComparisonText) inComparisonText.classList.add('hidden');
                button.disabled = false;

                // Update button styling for normal state
                button.classList.remove('bg-green-600', 'hover:bg-green-700');
                button.classList.add('bg-blue-600', 'hover:bg-blue-700');
            }
        });

        // Update remove button visibility
        removeButtons.forEach((button) => {
            if (this.currentProductInComparison) {
                button.classList.remove('hidden');
            } else {
                button.classList.add('hidden');
            }
        });
    }

    // Header Management Methods
    clearAllHeaders() {
        // Remove all headers from DOM except first column and template
        this.renderedHeaders.forEach((headerElement) => {
            if (headerElement.parentNode) {
                headerElement.remove();
            }
        });
        this.renderedHeaders.clear();
    }

    addHeader(product) {
        const headerRow = this.querySelector('thead tr');
        const headerTemplate = headerRow.querySelector('[data-product-header-template]');
        
        if (!headerTemplate) {
            console.error('Header template not found');
            return null;
        }

        const productData = this.productCache.get(product.handle) || product;
        const header = headerTemplate.cloneNode(true);
        header.classList.remove('hidden');
        header.dataset.productId = product.id;
        header.classList.add('comparison-product-header');

        // Create header content
        const container = header.querySelector('div') || header;
        container.className = '!p-4 relative h-full min-h-[300px]';

        const actionButtonHtml = product.isRecommendation
            ? `<button class="text-blue-600 hover:text-blue-700 transition-colors text-sm underline w-full text-center" 
                data-add-recommendation-to-comparison
                data-product-id="${product.id}"
                data-product-handle="${product.handle}"
                data-product-title="${productData.title}"
                data-product-image="${productData.featured_image?.url || product.image}"
                data-product-price="${productData.price}"
                data-product-url="${product.url || `/products/${product.handle}`}">
                ${window.themeVariables?.strings?.product_comparison?.add_to_comparison || 'Add to Comparison'}
            </button>`
            : `<button class="text-gray-500 hover:text-red-600 transition-colors text-sm underline w-full text-center" data-remove-product>
                ${window.themeVariables?.strings?.product_comparison?.remove || 'Remove'}
            </button>`;

        container.innerHTML = `
            <div class="aspect-square bg-gray-50 rounded-lg mb-3 p-2">
                <img src="${productData.featured_image?.url || product.image}" alt="${productData.title}" class="w-full h-full object-contain" loading="lazy">
            </div>
            <h3 class="font-semibold text-sm mb-2" style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${productData.title}</h3>
            <div class="text-lg font-bold mb-3">${this.formatPrice(productData.price)}</div>
            <a href="${product.url || `/products/${product.handle}`}" class="block w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors text-center mb-2">
                ${window.themeVariables?.strings?.product_comparison?.view_product || 'View Product'}
            </a>
            ${actionButtonHtml}
        `;

        // Add to DOM and track in state
        headerRow.appendChild(header);
        this.renderedHeaders.set(product.id, header);
        
        return header;
    }

    removeHeader(productId) {
        const headerElement = this.renderedHeaders.get(productId);
        if (headerElement && headerElement.parentNode) {
            headerElement.remove();
        }
        this.renderedHeaders.delete(productId);
    }

    syncHeaders() {
        // Get current product IDs that should be displayed
        const currentProductIds = new Set(this.comparisonProducts.map(p => p.id));
        
        // Remove headers for products that are no longer in comparison
        for (const [productId] of this.renderedHeaders) {
            if (!currentProductIds.has(productId)) {
                this.removeHeader(productId);
            }
        }
        
        // Add headers for new products
        this.comparisonProducts.forEach(product => {
            if (!this.renderedHeaders.has(product.id)) {
                this.addHeader(product);
            }
        });
    }

    renderTableView() {
        // Hide swiper, show table
        const swiperContainer = this.querySelector('[data-comparison-swiper]');
        const tableContainer = this.querySelector('[data-comparison-table]');

        if (swiperContainer) swiperContainer.style.display = 'none';
        if (tableContainer) tableContainer.style.display = 'block';

        // Destroy swiper if exists
        if (this.desktopSwiper) {
            this.desktopSwiper.destroy();
            this.desktopSwiper = null;
        }

        // Sync headers with current product state
        this.syncHeaders();

        // Render specs
        this.renderSpecs();
    }

    renderSpecs() {
        const specsConfig = this.getSpecsConfiguration();
        const tbody = this.querySelector('[data-specs-body]');
        tbody.innerHTML = '';

        specsConfig.specs.forEach((spec) => {
            // Check if any product has a value for this spec
            if (this.hasSpecData(spec)) {
                const row = this.createSpecRow(spec);
                if (row) tbody.appendChild(row);
            }
        });
    }

    createSpecRow(spec) {
        const tr = document.createElement('tr');
        tr.className = 'border-b hover:bg-gray-50 transition-colors';

        // Label cell
        const labelCell = document.createElement('td');
        labelCell.className = 'py-4 !pl-6 pr-4 text-left border-b border-gray-100 font-medium text-gray-900';

        if (spec.icon) {
            const container = document.createElement('div');
            container.className = 'flex items-center gap-3';

            // Get icon from hidden container if available
            const iconContainer = document.getElementById('mobile-icon-container');
            const iconElement = iconContainer?.querySelector(`[data-icon="${spec.icon}"]`);

            if (iconElement) {
                const iconClone = iconElement.cloneNode(true);
                iconClone.className = 'w-4 h-4 text-gray-500 flex-shrink-0';
                container.appendChild(iconClone);
            }

            const label = document.createElement('span');
            label.textContent = spec.label;
            container.appendChild(label);

            labelCell.appendChild(container);
        } else {
            labelCell.textContent = spec.label;
        }

        tr.appendChild(labelCell);

        // Current product value cell
        const currentCell = document.createElement('td');
        currentCell.className = 'p-4 text-center';
        const currentValue = this.getCurrentProductSpecValue(spec);
        currentCell.textContent = currentValue || '-';
        tr.appendChild(currentCell);

        // Add comparison product values
        this.comparisonProducts.forEach((product) => {
            const productData = this.productCache.get(product.handle) || product;
            const value = this.getSpecValue(spec, productData);

            const cell = document.createElement('td');
            cell.className = 'p-4 text-center';
            cell.textContent = value || '-';
            tr.appendChild(cell);
        });

        return tr;
    }

    renderMobileView() {
        const mobileSwiper = this.querySelector('[data-mobile-comparison-swiper]');
        if (!mobileSwiper) return;

        const wrapper = mobileSwiper.querySelector('.swiper-wrapper');

        // Keep current product slide, remove ALL other slides completely
        const existingSlides = wrapper.querySelectorAll('.swiper-slide:not(:first-child)');
        existingSlides.forEach((slide) => slide.remove());

        // Destroy existing swiper to reset state
        if (this.mobileSwiper) {
            this.mobileSwiper.destroy(true, true);
            this.mobileSwiper = null;
        }

        // Update current product specs to show all specs that ANY product has
        this.updateCurrentProductMobileSpecs();

        // Add comparison product slides
        const template = document.getElementById('comparison-product-card-template');

        this.comparisonProducts.forEach((product) => {
            const productData = this.productCache.get(product.handle) || product;
            const slide = template.content.cloneNode(true).firstElementChild;

            // Populate slide data
            slide.dataset.productId = product.id;
            const image = slide.querySelector('[data-product-image]');
            const title = slide.querySelector('[data-product-title]');
            const price = slide.querySelector('[data-product-price]');
            const link = slide.querySelector('[data-product-link]');
            const specsContainer = slide.querySelector('[data-product-specs]');

            if (image) {
                image.src = productData.featured_image?.url || product.image;
                image.alt = productData.title;
            }
            if (title) title.textContent = productData.title;
            if (price) price.textContent = this.formatPrice(productData.price);
            if (link) link.href = product.url || `/products/${product.handle}`;

            // Add specs
            if (specsContainer) {
                this.renderMobileSpecs(specsContainer, productData);
            }

            // Handle recommendation vs comparison button
            const removeButton = slide.querySelector('[data-remove-product]');
            if (removeButton && product.isRecommendation) {
                // Replace remove button with add to comparison button for recommendations
                removeButton.textContent = window.themeVariables?.strings?.product_comparison?.add_to_comparison || 'Add to Comparison';
                removeButton.className = 'text-blue-600 hover:text-blue-700 transition-colors text-sm underline w-full text-center';
                removeButton.removeAttribute('data-remove-product');
                removeButton.setAttribute('data-add-recommendation-to-comparison', '');
                removeButton.setAttribute('data-product-id', product.id);
                removeButton.setAttribute('data-product-handle', product.handle);
                removeButton.setAttribute('data-product-title', productData.title);
                removeButton.setAttribute('data-product-image', productData.featured_image?.url || product.image);
                removeButton.setAttribute('data-product-price', productData.price);
                removeButton.setAttribute('data-product-url', product.url || `/products/${product.handle}`);
            }

            wrapper.appendChild(slide);
        });

        // Initialize mobile swiper fresh
        this.mobileSwiper = new Swiper(mobileSwiper, {
            modules: [Pagination],
            slidesPerView: 1.1,
            spaceBetween: 16,
            centeredSlides: false,
            pagination: {
                el: '.swiper-pagination',
                clickable: true
            }
        });
    }

    updateCurrentProductMobileSpecs() {
        const currentProductSpecsContainer = this.querySelector('[data-current-product-specs]');
        if (!currentProductSpecsContainer) return;

        // Store the original spec values before we replace the container
        const originalSpecs = {};
        const specsConfig = this.getSpecsConfiguration();

        specsConfig.specs.forEach((spec) => {
            const originalValue = this.getCurrentProductSpecValue(spec);
            if (originalValue && originalValue !== '-') {
                originalSpecs[spec.label] = originalValue;
            }
        });

        // Now render with the new logic that shows all specs
        const mockCurrentProductData = {
            // We'll use the stored original specs
            originalSpecs: originalSpecs
        };

        this.renderMobileSpecs(currentProductSpecsContainer, mockCurrentProductData);
    }

    renderMobileSpecs(container, productData) {
        const specsConfig = this.getSpecsConfiguration();

        container.innerHTML = '';

        specsConfig.specs.forEach((spec) => {
            // Only show spec if ANY product has data for it
            if (this.hasSpecData(spec)) {
                let value;

                // Handle current product specs specially when we have stored values
                if (productData?.originalSpecs) {
                    value = productData.originalSpecs[spec.label] || null;
                } else {
                    value = this.getSpecValue(spec, productData);
                }

                const specRow = document.createElement('div');
                specRow.className = 'flex items-center justify-between py-2 border-b border-gray-100';

                const labelContainer = document.createElement('div');
                labelContainer.className = 'flex items-center gap-2';

                // Add icon if available
                if (spec.icon) {
                    const iconContainer = document.getElementById('mobile-icon-container');
                    const iconElement = iconContainer?.querySelector(`[data-icon="${spec.icon}"]`);

                    if (iconElement) {
                        const iconClone = iconElement.cloneNode(true);
                        iconClone.className =
                            'w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0';
                        const svg = iconClone.querySelector('svg');
                        if (svg) {
                            svg.setAttribute('class', 'w-4 h-4 text-blue-600');
                        }
                        labelContainer.appendChild(iconClone);
                    }
                }

                const label = document.createElement('span');
                label.className = 'text-sm font-medium text-gray-700';
                label.textContent = spec.label;
                labelContainer.appendChild(label);

                const valueEl = document.createElement('span');
                valueEl.className = 'text-sm text-gray-900 font-semibold';
                valueEl.textContent = value || '-';

                specRow.appendChild(labelContainer);
                specRow.appendChild(valueEl);
                container.appendChild(specRow);
            }
        });
    }

    getCurrentProductSpecValue(spec) {
        // First try to get value from mobile specs container (for mobile view)
        const currentSpecs = this.querySelector('[data-current-product-specs]');
        if (currentSpecs) {
            const specDivs = currentSpecs.querySelectorAll('div');
            for (const div of specDivs) {
                const label = div.querySelector('.text-gray-600')?.textContent;
                if (label === spec.label) {
                    const value = div.querySelector('.font-semibold')?.textContent;
                    if (value && value !== '-') {
                        return value;
                    }
                }
            }
        }

        // For desktop view, use cached current product data
        if (this.currentProductData) {
            return this.getSpecValue(spec, this.currentProductData);
        }

        return null;
    }

    async loadCurrentProductData() {
        // Try to get data from existing script first
        const productDataScript = document.querySelector('#product-comparison-data');
        if (productDataScript) {
            try {
                this.currentProductData = JSON.parse(productDataScript.textContent);
                return;
            } catch (error) {
                console.error('Failed to parse current product data:', error);
            }
        }

        // Fallback: fetch current product data
        try {
            const response = await fetch(`/products/${this.currentProductHandle}?view=comparison`);
            if (response.ok) {
                const html = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const dataScript = doc.querySelector('#product-comparison-data');

                if (dataScript) {
                    this.currentProductData = JSON.parse(dataScript.textContent);
                    this.productCache.set(this.currentProductHandle, this.currentProductData);
                }
            }
        } catch (error) {
            console.error('Failed to fetch current product data:', error);
        }
    }

    getSpecValue(spec, productData) {
        if (!productData) return null;

        if (spec.metafield_key === 'price') {
            return this.formatPrice(productData.price);
        }

        if (spec.metafield_key.startsWith('custom.') && productData.metafields) {
            const key = spec.metafield_key.replace('custom.', '');
            const value = productData.metafields[key];

            if (value) {
                return value + (spec.suffix || '');
            }
        }

        return null;
    }

    hasSpecData(spec) {
        // Check if current product has this spec
        const currentValue = this.getCurrentProductSpecValue(spec);
        if (currentValue && currentValue !== '-') {
            return true;
        }

        // Check if any comparison product has this spec
        for (const product of this.comparisonProducts) {
            const productData = this.productCache.get(product.handle) || product;
            const value = this.getSpecValue(spec, productData);
            if (value && value !== '-') {
                return true;
            }
        }

        return false;
    }

    getSpecsConfiguration() {
        const specsScript = this.querySelector('[data-comparison-specs]');
        if (!specsScript) return { specs: [] };

        try {
            return JSON.parse(specsScript.textContent);
        } catch (error) {
            console.error('Failed to parse specs configuration:', error);
            return { specs: [] };
        }
    }

    formatPrice(price) {
        if (!price) return '-';

        // Price is in cents
        const amount = price / 100;
        return new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    }

    showLoading(show) {
        const loadingOverlays = this.querySelectorAll('[data-loading]');
        loadingOverlays.forEach((overlay) => {
            overlay.classList.toggle('hidden', !show);
        });
    }

    disconnectedCallback() {
        if (this.desktopSwiper) {
            this.desktopSwiper.destroy();
        }
        if (this.mobileSwiper) {
            this.mobileSwiper.destroy();
        }

        // Clean up event listeners
        window.removeEventListener('resize', this.handleResize);
        clearTimeout(this.resizeTimeout);
    }
}

// Register custom element
customElements.define('product-comparison-section', ProductComparisonSection);

// Export for use in other modules
export { ProductComparisonSection };
